import { useEffect, useCallback } from 'react';
import { Modal } from 'reactstrap';
import FeatherIcon from 'feather-icons-react';
import { mediaKind, fileNameFromKey } from '../Helpers/format';

/**
 * A single 64x48 thumbnail. `src` may be null while the signed URL is loading.
 * `onOpen` makes it a button (lightbox); without it the thumb is static.
 */
const MediaThumb = ({ src, name, kind, pending = false, onOpen }) => {
  const k = kind || mediaKind(name);
  const label = pending ? `${name} (not uploaded yet)` : name;

  const inner = (
    <>
      {k === 'image' && src ? (
        <img src={src} alt="" loading="lazy" />
      ) : k === 'video' && src ? (
        <video src={src} muted preload="metadata" />
      ) : (
        <FeatherIcon icon={k === 'video' ? 'film' : k === 'image' ? 'image' : 'file'} size={18} />
      )}
      {k === 'video' && <span className="pm-thumb__badge">video</span>}
      {pending && <span className="pm-thumb__pending">new</span>}
    </>
  );

  if (onOpen) {
    return (
      <button type="button" className="pm-thumb" onClick={onOpen} aria-label={`Open ${label}`} title={label}>
        {inner}
      </button>
    );
  }
  return (
    <span className="pm-thumb pm-thumb--static" title={label} aria-label={label} role="img">
      {inner}
    </span>
  );
};

/**
 * Thumbnail strip for a list of S3 keys. `signedUrls` is { [key]: url|null }.
 * Clicking a thumb calls onOpen(index).
 */
const MediaGallery = ({ keys = [], signedUrls = {}, onOpen, emptyText = 'No media' }) => {
  if (!keys.length) return <span className="text-muted small">{emptyText}</span>;
  return (
    <div className="pm-thumbs">
      {keys.map((key, i) => (
        <MediaThumb
          key={key}
          src={signedUrls[key] || null}
          name={fileNameFromKey(key)}
          kind={mediaKind(key)}
          onOpen={onOpen ? () => onOpen(i) : undefined}
        />
      ))}
    </div>
  );
};

/**
 * Minimal lightbox: items = [{ key, url, name? }], index controlled by parent.
 */
const MediaLightbox = ({ isOpen, items = [], index = 0, onClose, onIndexChange }) => {
  const item = items[index];
  const count = items.length;

  const go = useCallback(
    (delta) => {
      if (!count || !onIndexChange) return;
      onIndexChange((index + delta + count) % count);
    },
    [count, index, onIndexChange]
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowRight') go(1);
      else if (e.key === 'ArrowLeft') go(-1);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, go]);

  if (!item) return null;
  const kind = mediaKind(item.key || item.name);
  const name = item.name || fileNameFromKey(item.key);

  return (
    <Modal isOpen={isOpen} toggle={onClose} size="xl" centered className="pm-lightbox" zIndex={1080} scrollable={false}>
      <div className="pm-lightbox__bar">
        <span className="pm-lightbox__name" title={name}>
          {name}
          {count > 1 ? ` · ${index + 1} / ${count}` : ''}
        </span>
        <div className="d-flex gap-2">
          {count > 1 && (
            <>
              <button type="button" className="pm-icon-btn" onClick={() => go(-1)} aria-label="Previous">
                <FeatherIcon icon="chevron-left" size={18} />
              </button>
              <button type="button" className="pm-icon-btn" onClick={() => go(1)} aria-label="Next">
                <FeatherIcon icon="chevron-right" size={18} />
              </button>
            </>
          )}
          {item.url && (
            <a
              className="pm-icon-btn"
              href={item.url}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Open in a new tab"
              title="Open in a new tab"
            >
              <FeatherIcon icon="external-link" size={18} />
            </a>
          )}
          <button type="button" className="pm-icon-btn" onClick={onClose} aria-label="Close preview">
            <FeatherIcon icon="x" size={18} />
          </button>
        </div>
      </div>
      <div className="pm-lightbox__stage">
        {!item.url ? (
          <span className="text-light small">Preview unavailable</span>
        ) : kind === 'video' ? (
          <video src={item.url} controls autoPlay playsInline />
        ) : (
          <img src={item.url} alt={name} />
        )}
      </div>
    </Modal>
  );
};

export { MediaThumb, MediaGallery, MediaLightbox };
